import { batchActions } from 'redux-batched-actions';
import { all, call, fork, put, take, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import {
  Presentation,
  PresentationProperty,
  AffectedDevice,
  Application,
  Theme,
  Domain,
  OAuthCredentials,
} from '@raydiant/api-client-js';
import * as appActions from '../../actions/applications';
import * as presActions from '../../actions/presentations';
import * as themeActions from '../../actions/themes';
import * as domainActions from '../../actions/domains';
import miraClient from '../../clients/miraClient';
import logger from '../../logger';
import { getOAuthProps } from '../../utilities/oauthProps';
import * as actions from './actions';

type LoadPresentationPageAction = ReturnType<
  typeof actions.loadPresentationPage
>;

type LoadOAuthPrefillAction = ReturnType<typeof actions.loadOAuthPrefill>;

// Verify a stored credential against the app's own verify endpoint before it is
// offered as a prefill. This is a direct call to the app backend (not mira-api),
// so a dead/expired credential is never injected. Any failure — network error
// or non-2xx — is treated as "not verified" (read-only; never deletes).
const verifyCredential = async (
  prop: PresentationProperty,
  value: string,
): Promise<boolean> => {
  if (!prop.verify_url || !prop.verify_qs_param) return false;
  try {
    const url = new URL(prop.verify_url);
    url.searchParams.set(prop.verify_qs_param, value);
    const res = await fetch(url.toString());
    return res.ok;
  } catch (error) {
    return false;
  }
};

const loadOAuthPrefill = function* ({ payload }: LoadOAuthPrefillAction) {
  const { applicationId, presentationProperties } = payload;
  const oauthProps = getOAuthProps({ presentationProperties });
  // Always mark resolved when this cycle ends (props-empty early return, GET
  // failure, or normal completion) so the form-edit freeze can engage. Until
  // then the freeze is gated off, which prevents a programmatic onChange from
  // freezing the overlay before the async GET+verify lands.
  try {
    if (oauthProps.length === 0) return;

    let credentials: OAuthCredentials;
    try {
      credentials = yield call(() =>
        miraClient.getOAuthCredentials(applicationId),
      );
    } catch (error: any) {
      // GET failure → no prefill. Silent: prefill is a convenience, never blocking.
      logger.error(error);
      return;
    }

    for (const prop of oauthProps) {
      const value = credentials[prop.name];
      if (value === undefined || value === null || value === '') continue;

      const verified: boolean = yield call(verifyCredential, prop, value);
      if (verified) {
        yield put(
          actions.setOAuthPrefill({
            applicationId,
            propName: prop.name,
            value,
          }),
        );
      }
    }
  } finally {
    yield put(actions.markOAuthPrefillResolved({ applicationId }));
  }
};

const watchLoadOAuthPrefill = function* () {
  yield takeLatest(getType(actions.loadOAuthPrefill), loadOAuthPrefill);
};

const watchLoadPresentationPage = function* () {
  while (true) {
    const { payload }: LoadPresentationPageAction = yield take(
      getType(actions.loadPresentationPage),
    );

    const { presentationId } = payload;

    try {
      yield put(actions.loadPresentationPageAsync.request());

      const [presentations, affectedDevices, applications, themes, domain]: [
        Presentation[],
        AffectedDevice[],
        Application[],
        Theme[],
        Domain | null,
      ] = yield all([
        call(() =>
          presentationId
            ? miraClient.getPresentations({ ids: [presentationId] })
            : [],
        ),
        call(() =>
          presentationId
            ? miraClient.getAffectedDevices({
                presentationIds: [presentationId],
              })
            : [],
        ),
        call(() => miraClient.getApplications()),
        call(() => miraClient.getThemes()),
        call(() => miraClient.getDomain()),
      ]);

      yield put(
        batchActions([
          actions.loadPresentationPageAsync.success({ affectedDevices }),
          appActions.fetchApplicationsAsync.success(applications),
          presActions.fetchPresentationsAsync.success(presentations),
          themeActions.getThemesAsync.success(themes),
          ...(domain ? [domainActions.fetchDomainAsync.success(domain)] : []),
        ]),
      );
    } catch (error: any) {
      logger.error(error);
      yield put(actions.loadPresentationPageAsync.failure(error));
    }
  }
};

export default all([
  fork(watchLoadPresentationPage),
  fork(watchLoadOAuthPrefill),
]);
